import { debounce } from './utils/utils';
import { getDateFromAge } from './utils/date';

export const FLEX_OPTION_MAX_AGE = 38;
const AGE_DEFAULT = 29;
const AGE_MIN_DEFAULT = 1;
const AGE_MAX_DEFAULT = 99;

const tariffServiceUrl = window.backendApiUrl + '/tariff';

const defaultOptions = {
  flex: false,
  care: false,
  bonus: false
};
/**
 *
 * @param {"clinic"|"dental"} type
 * @param {number} age
 * @returns
 */
const getTariffData = async (type, age) => {
  try {
    const response = await fetch(`${tariffServiceUrl}/otto/${type}/quotes?date_of_birth=${getDateFromAge(age)}`);
    if (!response.ok) {
      return false;
    }
    const data = await response.json();
    return data;
  } catch (e) {
    return false;
  }
};
/**
 * Maps Parameters to a tariffs identfifier
 * @param {string} type
 * @param {string} baseTariff
 * @param {any} options
 * @returns {string}
 */
const getTariffName = (type, baseTariff, age, options = {}) => {
  let suffix = '';
  let prefix = '';
  let tariffName = baseTariff.replace('tud-', '').replace('tuc-', '');

  if (type === 'dental') {
    prefix = 'tud-';
  } else if (type === 'clinic') {
    prefix = 'tuc-';
  }

  if (type === 'dental' && baseTariff.indexOf('v2') > -1) {
    tariffName = tariffName.replace('-v2', '');
    const hasCare = options.care;
    const hasBonus = tariffName !== 'first' && options.bonus;
    if (hasCare && hasBonus) {
      suffix = '-premium-v2';
    } else if (hasCare) {
      suffix = '-care-v2';
    } else if (hasBonus) {
      suffix = '-bonus-v2';
    } else {
      suffix = '-v2';
    }
  } else if (type === 'clinic' && baseTariff.indexOf('zweibett') > -1 && age <= FLEX_OPTION_MAX_AGE && options.flex) {
    tariffName = 'twen';
  }
  return `${prefix}${tariffName}${suffix}`;
};

class Tariff {
  constructor(tariffName, tariffPremium, tariffOptions, tariffOptionPremiums) {
    this._name = tariffName;
    this._options = tariffOptions;
    this._optionPremiums = tariffOptionPremiums;
    this._premium = tariffPremium;
    this._changeHandlers = [];
  }

  subscribe(onChange) {
    this._changeHandlers.push(onChange);
  }
  changeHandler() {
    this._changeHandlers.forEach(handler => handler());
  }
  getOption(option) {
    return this._options[option];
  }
  setOption(option, value) {
    if (this._options[option] === value) {
      return;
    }
    this._options[option] = value;
  }
  getOptions() {
    return this._options;
  }
  setPremium(newPremium) {
    if (this._premium === newPremium) {
      return;
    }
    this._premium = newPremium;
    this.changeHandler();
  }
  getPremium() {
    return this._premium;
  }
  setOptionPremiums(premiums) {
    this._optionPremiums = premiums;
  }
  getOptionPremium(option) {
    return this._optionPremiums[option];
  }
  getName() {
    return this._name;
  }
  setName(name) {
    if (name) {
      this._name = name;
    }
  }
}

/**
 *
 * @param {number} defaultAge
 * @param {number} minAge
 * @param {number} maxAge
 */
const QQC = (defaultAge = AGE_DEFAULT, minAge = AGE_MIN_DEFAULT, maxAge = AGE_MAX_DEFAULT) => {
  let age = defaultAge;

  const tariffData = {};
  const tariffs = {};
  const ageChangeHandlers = [];

  /* PUBLIC METHODS */
  const subscribe = onChange => {
    ageChangeHandlers.push(onChange);
  };
  const onAgeChanged = () => {
    ageChangeHandlers.forEach(handler => handler());
  };

  const getOptionPremiums = async (type, tariffName, tariffOptions) => {
    const optionPremiums = {};
    const basePremium = await getPremium(type, getTariffName(type, tariffName, age, {}));

    for (const optionName in tariffOptions) {
      const optionsObject = {};
      optionsObject[optionName] = true;
      const optionPremium = (await getPremium(type, getTariffName(type, tariffName, age, optionsObject))) - basePremium;
      optionPremiums[optionName] = optionPremium;
    }
    return optionPremiums;
  };

  const updateTariffs = debounce(async () => {
    for (const type in tariffData) {
      const data = await getTariffData(type, age);
      if (!data) {
        continue;
      }
      tariffData[type] = data;
      for (const tariffName in tariffs[type]) {
        const tariff = await tariffs[type][tariffName];
        if (age < FLEX_OPTION_MAX_AGE) {
          tariff.setOption('flex', true);
        }
        const tariffOptions = tariff.getOptions();
        tariff.setName(getTariffName(type, tariffName, age, tariffOptions));

        tariff.setOptionPremiums(await getOptionPremiums(type, tariffName, tariffOptions));
        tariff.setPremium(await getPremium(type, tariff.getName()));
      }
    }
  }, 300);

  const setAge = async newAge => {
    if (!newAge) {
      age = defaultAge;
    } else {
      let ageValue = parseInt(newAge) || defaultAge;
      if (ageValue < minAge) {
        ageValue = minAge;
      } else if (ageValue > maxAge) {
        ageValue = maxAge;
      }
      if (ageValue === age) {
        return;
      }
      age = ageValue;
    }

    updateTariffs();
    onAgeChanged();
  };
  const addTariff = async (type, name, options = { ...defaultOptions }) => {
    if (!['dental', 'clinic'].includes(type)) {
      console.warn('invalid qqc-type');
      return;
    }
    if (!tariffs[type]) {
      tariffs[type] = {};
      tariffData[type] = getTariffData(type, age);
    }
    if (!tariffs[type][name]) {
      if (age < FLEX_OPTION_MAX_AGE) {
        options.flex = true;
      }
      const tariffFullName = getTariffName(type, name, age, options);
      tariffs[type][name] = new Promise(async resolve => {
        resolve(new Tariff(tariffFullName, await getPremium(type, tariffFullName), options, await getOptionPremiums(type, name, options)));
      });
    }
    return tariffs[type][name];
  };

  /**
   *
   * @param {string} type
   * @param {string} name
   * @returns Promise<Tariff>
   */
  const getTariff = (type, name) => tariffs[type][name];

  const getPremium = async (type, tariffFullName) => {
    const data = await tariffData[type];
    if (!Array.isArray(data)) {
      return null;
    }
    const tariff = data.find(tariff => tariff.name === tariffFullName);
    return tariff?.premium ?? null;
  };
  const setTariffOption = async (type, tariffName, option, value) => {
    const tariff = await tariffs[type][tariffName];
    tariff.setOption(option, value);
    tariff.setName(getTariffName(type, tariffName, age, tariff.getOptions()));
    tariff.setPremium(await getPremium(type, tariff.getName()));
  };
  return {
    setAge,
    getAge: () => age,
    getMinAge: () => minAge,
    getMaxAge: () => maxAge,
    addTariff,
    getTariff,
    setTariffOption,
    subscribe
  };
};
export default QQC;
